class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.announcementBar = this.querySelector("#wt-announcement__container");
    this.announcementInterval = null;
    this.marqueeRafId = null;
    this.marqueePosition = 0;
    this.marqueeLastTime = null;
    this.marqueePaused = false;

    this.changeAnnouncement = this.changeAnnouncement.bind(this);
    this.previousAnnouncement = this.previousAnnouncement.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.hideForOneDay = this.hideForOneDay.bind(this);
    this.restartInterval = this.restartInterval.bind(this);
    this.isMobileView = this.isMobileView.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.stepMarquee = this.stepMarquee.bind(this);
    this.handleMarqueeEnter = this.handleMarqueeEnter.bind(this);
    this.handleMarqueeLeave = this.handleMarqueeLeave.bind(this);
  }

  connectedCallback() {
    const isMarquee = this.announcementBar?.classList.contains(
      "wt-announcement__container--marquee",
    );

    if (!isMarquee) {
      this.announcementInterval = setInterval(this.changeAnnouncement, 5000);

      if (this.announcementBar) {
        this.announcementBar.addEventListener(
          "touchstart",
          this.handleTouchStart,
          { passive: true },
        );
        this.announcementBar.addEventListener(
          "touchmove",
          this.handleTouchMove,
          { passive: true },
        );
        this.announcementBar.addEventListener("touchend", this.handleTouchEnd, {
          passive: true,
        });
        this.announcementBar.addEventListener(
          "touchstart",
          () => clearInterval(this.announcementInterval),
          { passive: true },
        );
        this.announcementBar.addEventListener(
          "touchend",
          this.restartInterval,
          { passive: true },
        );
        this.announcementBar.style.transition = "transform 0.5s ease-in-out";
      }
    } else {
      this.initTrackMarquee();
      window.addEventListener("load", () => this.initTrackMarquee(), {
        once: true,
      });
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.initTrackMarquee());
      }
      this.announcementBar?.addEventListener(
        "mouseenter",
        this.handleMarqueeEnter,
      );
      this.announcementBar?.addEventListener(
        "mouseleave",
        this.handleMarqueeLeave,
      );
    }

    this.closeButton = this.querySelector(".wt-announcement__close");
    if (this.closeButton)
      this.closeButton.addEventListener("click", this.hideForOneDay);

    window.addEventListener("resize", this.handleResize);
  }

  getMarqueeDurationMs() {
    if (!this.announcementBar) return 25000;

    const raw = getComputedStyle(this.announcementBar)
      .getPropertyValue("--time")
      .trim()
      .replace("s", "");
    const seconds = parseFloat(raw);

    if (Number.isFinite(seconds) && seconds > 0) return seconds * 1000;
    return 25000;
  }

  initTrackMarquee() {
    const track = this.querySelector(".wt-announcement__marquee-track");
    const firstSet = track?.querySelector(".wt-announcement__marquee-set");
    if (!track || !firstSet || !this.announcementBar) return;
    if (track.dataset.marqueeRunning === "1") return;

    if (this.marqueeRafId) {
      cancelAnimationFrame(this.marqueeRafId);
      this.marqueeRafId = null;
    }

    track.style.animation = "none";
    track.style.webkitAnimation = "none";

    const loopWidth = firstSet.getBoundingClientRect().width;
    if (loopWidth <= 0) return;

    track.dataset.loopWidth = String(loopWidth);
    this.marqueePosition = 0;
    this.marqueeLastTime = null;
    this.marqueeRafId = requestAnimationFrame(this.stepMarquee);
  }

  stepMarquee(time) {
    const track = this.querySelector(".wt-announcement__marquee-track");
    if (!track || !this.announcementBar) return;

    const loopWidth = parseFloat(track.dataset.loopWidth || "0");
    if (loopWidth <= 0) {
      this.initTrackMarquee();
      return;
    }

    if (this.marqueeLastTime == null) this.marqueeLastTime = time;
    const delta = time - this.marqueeLastTime;
    this.marqueeLastTime = time;

    if (!this.marqueePaused) {
      const isRtl = document.documentElement.getAttribute("dir") === "rtl";
      const distance = (loopWidth / this.getMarqueeDurationMs()) * delta;

      if (isRtl) this.marqueePosition += distance;
      else this.marqueePosition -= distance;

      if (this.marqueePosition <= -loopWidth) this.marqueePosition += loopWidth;
      if (this.marqueePosition >= loopWidth) this.marqueePosition -= loopWidth;
    }

    track.style.transform = `translate3d(${this.marqueePosition}px, 0, 0)`;
    this.marqueeRafId = requestAnimationFrame(this.stepMarquee);
  }

  handleMarqueeEnter() {
    this.marqueePaused = true;
    this.announcementBar?.classList.add("is-paused");
  }

  handleMarqueeLeave() {
    this.marqueePaused = false;
    this.announcementBar?.classList.remove("is-paused");
    this.marqueeLastTime = null;
  }

  handleResize() {
    if (this.announcementBar?.classList.contains("wt-announcement__container--marquee")) {
      this.initTrackMarquee();
      return;
    }

    if (window.innerWidth >= 900 && this.announcementBar) {
      this.announcementBar.style.transform = "translateX(0)";
      this.currentIndex = 0;
    }
  }

  isMobileView() {
    return window.innerWidth < 900;
  }

  restartInterval() {
    clearInterval(this.announcementInterval);
    this.announcementInterval = setInterval(this.changeAnnouncement, 5000);
  }

  hideForOneDay() {
    const oneDayLater = new Date().getTime() + 24 * 60 * 60 * 1000;
    localStorage.setItem("wt-announcement-hidden", oneDayLater);
    this.style.display = "none";
    if (this.marqueeRafId) cancelAnimationFrame(this.marqueeRafId);
  }

  changeAnnouncement() {
    if (!this.isMobileView()) return;

    const totalSlides = this.announcementBar?.children.length;
    this.currentIndex = (this.currentIndex + 1) % totalSlides;
    const newPosition = -(this.currentIndex * 100);

    if (this.announcementBar)
      this.announcementBar.style.transform = `translateX(${newPosition}vw)`;

    if (this.currentIndex === 0 && this.announcementBar) {
      setTimeout(() => {
        this.announcementBar.style.transition = "none";
        this.announcementBar.style.transform = "translateX(0)";
        this.currentIndex = 0;
        setTimeout(() => {
          this.announcementBar.style.transition = "transform 0.5s ease-in-out";
        }, 0);
      }, 490);
    }
  }

  previousAnnouncement() {
    const totalSlides = this.announcementBar?.children.length;
    this.currentIndex = (this.currentIndex - 1 + totalSlides) % totalSlides;
    const newPosition = -(this.currentIndex * 100);
    if (this.announcementBar)
      this.announcementBar.style.transform = `translateX(${newPosition}vw)`;
  }

  handleTouchStart(e) {
    if (this.isMobileView()) this.touchStartX = e.touches[0].clientX;
  }

  handleTouchMove(e) {
    if (this.isMobileView()) this.touchEndX = e.touches[0].clientX;
  }

  handleTouchEnd() {
    const swipeThreshold = 30;
    if (Math.abs(this.touchEndX - this.touchStartX) > swipeThreshold) {
      if (this.touchEndX < this.touchStartX) this.changeAnnouncement();
      else this.previousAnnouncement();
    }
  }

  disconnectedCallback() {
    clearInterval(this.announcementInterval);
    if (this.marqueeRafId) cancelAnimationFrame(this.marqueeRafId);
    if (this.announcementBar) {
      this.announcementBar.removeEventListener("touchstart", this.handleTouchStart);
      this.announcementBar.removeEventListener("touchmove", this.handleTouchMove);
      this.announcementBar.removeEventListener("touchend", this.handleTouchEnd);
      this.announcementBar.removeEventListener(
        "mouseenter",
        this.handleMarqueeEnter,
      );
      this.announcementBar.removeEventListener(
        "mouseleave",
        this.handleMarqueeLeave,
      );
    }
    window.removeEventListener("resize", this.handleResize);
  }
}

customElements.define("announcement-bar", AnnouncementBar);
